import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

let codeMirrorLoadPromise;
let vimConfigured = false;
let hasShownClipboardWarning = false;
let hasShownPasteWarning = false;
let lastKnownSystemClipboardText = null;
let pendingSystemClipboardWrites = 0;

const normalizeClipboardText = (text) => String(text || "").replace(/\r\n?/g, "\n");

const copyTextWithTextarea = (text) => {
  const activeElement = document.activeElement;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let didCopy = false;
  try {
    didCopy = document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
    activeElement?.focus?.();
  }

  return didCopy;
};

const writeSystemClipboard = async (text) => {
  if (!text) return;

  pendingSystemClipboardWrites += 1;
  try {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        lastKnownSystemClipboardText = normalizeClipboardText(text);
        return;
      }
    } catch (error) {
      if (copyTextWithTextarea(text)) {
        lastKnownSystemClipboardText = normalizeClipboardText(text);
        return;
      }

      if (!hasShownClipboardWarning) {
        hasShownClipboardWarning = true;
        console.warn("Unable to sync Vim register to the system clipboard:", error);
      }
    }

    if (copyTextWithTextarea(text)) {
      lastKnownSystemClipboardText = normalizeClipboardText(text);
      return;
    }

    if (!hasShownClipboardWarning) {
      hasShownClipboardWarning = true;
      console.warn("Unable to sync Vim register to the system clipboard.");
    }
  } finally {
    pendingSystemClipboardWrites = Math.max(0, pendingSystemClipboardWrites - 1);
  }
};

const readSystemClipboard = async () => {
  try {
    if (navigator.clipboard?.readText) {
      return await navigator.clipboard.readText();
    }
  } catch (error) {
    if (!hasShownPasteWarning) {
      hasShownPasteWarning = true;
      console.warn("Unable to read from the system clipboard:", error);
    }
    return null;
  }

  if (!hasShownPasteWarning) {
    hasShownPasteWarning = true;
    console.warn("Unable to read from the system clipboard.");
  }
  return null;
};

const getUnnamedVimRegister = (CodeMirror) =>
  CodeMirror.Vim.getRegisterController().getRegister('"');

const getVimRegisterSnapshot = (CodeMirror) => {
  const register = getUnnamedVimRegister(CodeMirror);
  return {
    text: register.toString(),
    linewise: Boolean(register.linewise),
    blockwise: Boolean(register.blockwise),
  };
};

const vimRegisterSnapshotsMatch = (first, second) =>
  first &&
  second &&
  normalizeClipboardText(first.text) === normalizeClipboardText(second.text) &&
  first.linewise === second.linewise &&
  first.blockwise === second.blockwise;

const clipboardTextMatchesVimRegister = (clipboardText, registerSnapshot) => {
  const normalizedClipboardText = normalizeClipboardText(clipboardText);
  const normalizedRegisterText = normalizeClipboardText(registerSnapshot.text);

  return (
    normalizedClipboardText === normalizedRegisterText ||
    (registerSnapshot.linewise &&
      normalizedClipboardText === `${normalizedRegisterText}\n`)
  );
};

const setVimRegisterFromClipboardText = (CodeMirror, text) => {
  getUnnamedVimRegister(CodeMirror).setText(
    normalizeClipboardText(text),
    false,
    false,
  );
};

const shouldUseSystemClipboardText = (clipboardText, registerSnapshot) => {
  const normalizedClipboardText = normalizeClipboardText(clipboardText);

  return (
    pendingSystemClipboardWrites === 0 &&
    !clipboardTextMatchesVimRegister(clipboardText, registerSnapshot) &&
    normalizedClipboardText !== lastKnownSystemClipboardText
  );
};

const configureSystemClipboardPaste = (CodeMirror, cm) => {
  cm.getWrapperElement().addEventListener(
    "keydown",
    async (event) => {
      if (
        event.target.closest?.(".CodeMirror-dialog") ||
        (event.key !== "p" && event.key !== "P") ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }

      const vimState = cm.state.vim;
      if (
        cm.getOption("keyMap") !== "vim" ||
        !vimState ||
        vimState.insertMode ||
        vimState.inputState?.operator
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const registerSnapshot = getVimRegisterSnapshot(CodeMirror);
      if (pendingSystemClipboardWrites > 0) {
        CodeMirror.Vim.handleKey(cm, event.key);
        return;
      }

      const clipboardText = await readSystemClipboard();
      if (clipboardText == null) {
        CodeMirror.Vim.handleKey(cm, event.key);
        return;
      }

      if (shouldUseSystemClipboardText(clipboardText, registerSnapshot)) {
        setVimRegisterFromClipboardText(CodeMirror, clipboardText);
      }
      lastKnownSystemClipboardText = normalizeClipboardText(clipboardText);
      CodeMirror.Vim.handleKey(cm, event.key);
    },
    true,
  );
};

const isVimCommandMode = (cm) => {
  const vimState = cm.state.vim;
  return (
    cm.getOption("keyMap") === "vim" &&
    vimState &&
    !vimState.insertMode &&
    !vimState.inputState?.operator
  );
};

const isSpaceKey = (event) =>
  event.key === " " || event.key === "Spacebar" || event.code === "Space";

const selectAllContent = (cm) => {
  cm.setSelection(
    { line: cm.firstLine(), ch: 0 },
    { line: cm.lastLine(), ch: cm.getLine(cm.lastLine()).length },
  );
};

// Line comments for the drawing editors (Python plots and SVG). Markdown has no
// line comment syntax, so the shortcut leaves it untouched.
const lineCommentStyles = {
  python: { open: "# ", match: /^(\s*)#\s?/, close: "" },
  svg: { open: "<!-- ", match: /^(\s*)<!--\s?(.*?)\s?-->\s*$/, close: " -->" },
};

export const toggleLineComments = (lines, syntaxMode) => {
  const style = lineCommentStyles[syntaxMode];
  if (!style) return null;
  const filled = lines.filter((line) => line.trim());
  if (!filled.length) return null;
  const commented = filled.every((line) => style.match.test(line));
  if (commented) {
    return lines.map((line) => {
      const match = line.match(style.match);
      if (!match) return line;
      return style.close ? `${match[1]}${match[2]}` : line.replace(style.match, "$1");
    });
  }
  // Comment at the shallowest indentation so the block keeps its shape.
  const indent = Math.min(...filled.map((line) => line.match(/^\s*/)[0].length));
  return lines.map((line) => (line.trim()
    ? `${line.slice(0, indent)}${style.open}${line.slice(indent)}${style.close}`
    : line));
};

const toggleCommentInEditor = (CodeMirror, cm) => {
  const syntaxMode = cm.state.sunnySyntaxMode;
  if (!lineCommentStyles[syntaxMode]) return false;
  const visual = Boolean(cm.state.vim?.visualMode);
  const ranges = visual ? cm.listSelections() : [{ anchor: cm.getCursor(), head: cm.getCursor() }];
  const cursor = cm.getCursor();
  cm.operation(() => {
    ranges.forEach(({ anchor, head }) => {
      const first = Math.min(anchor.line, head.line);
      let last = Math.max(anchor.line, head.line);
      // A characterwise selection ending at column 0 does not include that line.
      const end = anchor.line > head.line || (anchor.line === head.line && anchor.ch > head.ch) ? anchor : head;
      if (visual && !cm.state.vim.visualLine && last > first && end.ch === 0) last -= 1;
      const lines = [];
      for (let line = first; line <= last; line += 1) lines.push(cm.getLine(line));
      const next = toggleLineComments(lines, syntaxMode);
      if (!next) return;
      cm.replaceRange(next.join("\n"), { line: first, ch: 0 }, { line: last, ch: cm.getLine(last).length }, "+comment");
    });
  });
  if (visual) CodeMirror.Vim.exitVisualMode(cm);
  cm.setCursor({ line: Math.min(cursor.line, cm.lastLine()), ch: 0 });
  return true;
};

const configureVimLeaderShortcuts = (CodeMirror, cm) => {
  let waitingForLeaderKey = false;
  let leaderTimeout = null;

  const clearLeader = () => {
    waitingForLeaderKey = false;
    window.clearTimeout(leaderTimeout);
    leaderTimeout = null;
  };

  cm.getWrapperElement().addEventListener(
    "keydown",
    (event) => {
      if (event.target.closest?.(".CodeMirror-dialog") || event.metaKey || event.ctrlKey || event.altKey) {
        clearLeader();
        return;
      }

      if (!isVimCommandMode(cm)) {
        clearLeader();
        return;
      }

      if (isSpaceKey(event)) {
        event.preventDefault();
        event.stopPropagation();
        waitingForLeaderKey = true;
        window.clearTimeout(leaderTimeout);
        leaderTimeout = window.setTimeout(clearLeader, 900);
        return;
      }

      if (!waitingForLeaderKey) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        clearLeader();
        CodeMirror.Vim.handleEx(cm, "nohlsearch");
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "w") {
        event.preventDefault();
        event.stopPropagation();
        clearLeader();
        if (!cm.state.sunnyOnLeaderWrite?.(cm.getValue())) {
          cm.state.sunnyOnCloseModal?.();
        }
        return;
      }

      if (key === "a") {
        event.preventDefault();
        event.stopPropagation();
        clearLeader();
        selectAllContent(cm);
        return;
      }

      if (event.key === "/" || event.code === "Slash") {
        clearLeader();
        if (toggleCommentInEditor(CodeMirror, cm)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      clearLeader();
    },
    true,
  );
};

const getVimKey = (firstArg, secondArg) => secondArg ?? firstArg;

const shouldSyncVimOperatorToClipboard = (key) =>
  ["y", "Y", "d", "D", "x", "X"].includes(key);

const configureVimClipboard = (CodeMirror, cm) => {
  let pendingClipboardOperator = null;

  configureSystemClipboardPaste(CodeMirror, cm);
  configureVimLeaderShortcuts(CodeMirror, cm);

  cm.on("vim-keypress", (firstArg, secondArg) => {
    const key = getVimKey(firstArg, secondArg);
    if (shouldSyncVimOperatorToClipboard(key) && !pendingClipboardOperator) {
      pendingClipboardOperator = {
        registerName: cm.state.vim?.inputState?.registerName,
        before: getVimRegisterSnapshot(CodeMirror),
      };
    }
    if (!pendingClipboardOperator) return;

    window.setTimeout(() => {
      const vimState = cm.state.vim;
      const operatorPending = vimState?.inputState?.operator;
      if (operatorPending || !pendingClipboardOperator) return;

      const pendingOperator = pendingClipboardOperator;
      pendingClipboardOperator = null;
      if (pendingOperator.registerName === "_") return;

      const current = getVimRegisterSnapshot(CodeMirror);
      if (
        !current.text ||
        vimRegisterSnapshotsMatch(pendingOperator.before, current)
      ) {
        return;
      }

      writeSystemClipboard(current.text);
    }, 0);
  });
};

const vimTextObjectRange = (cm, startIndex, endIndex, vim) => {
  if (startIndex == null || endIndex == null || startIndex >= endIndex) {
    return null;
  }

  const start = cm.posFromIndex(startIndex);
  const end = cm.posFromIndex(endIndex);
  if (!vim.visualMode) return [start, end];

  return [start, cm.posFromIndex(endIndex - 1)];
};

const getVimWordRange = (cm, head, includeAround) => {
  const text = cm.getValue();
  if (!text) return null;

  const characterType = (character) => {
    if (/\s/u.test(character)) return "space";
    if (/[\p{L}\p{N}_]/u.test(character)) return "word";
    return "punctuation";
  };

  const cursorIndex = Math.min(cm.indexFromPos(head), text.length - 1);
  const type = characterType(text[cursorIndex]);
  let start = cursorIndex;
  let end = cursorIndex + 1;

  while (start > 0 && characterType(text[start - 1]) === type) start -= 1;
  while (end < text.length && characterType(text[end]) === type) end += 1;

  if (includeAround && type !== "space") {
    const trailingStart = end;
    while (end < text.length && /\s/u.test(text[end])) end += 1;
    if (end === trailingStart) {
      while (start > 0 && /\s/u.test(text[start - 1])) start -= 1;
    }
  }

  return { start, end };
};

const getVimBlockRange = (cm, head, opening, includeAround) => {
  const closingByOpening = { "(": ")", "[": "]", "{": "}", "<": ">" };
  const closing = closingByOpening[opening];
  const text = cm.getValue();
  const cursorIndex = cm.indexFromPos(head);
  let depth = 0;
  let start = null;

  for (let index = cursorIndex; index >= 0; index -= 1) {
    if (text[index] === closing) {
      depth += 1;
    } else if (text[index] === opening) {
      if (depth === 0) {
        start = index;
        break;
      }
      depth -= 1;
    }
  }

  if (start == null) return null;

  depth = 0;
  let end = null;
  for (let index = start + 1; index < text.length; index += 1) {
    if (text[index] === opening) {
      depth += 1;
    } else if (text[index] === closing) {
      if (depth === 0) {
        end = index;
        break;
      }
      depth -= 1;
    }
  }

  if (end == null || cursorIndex > end) return null;
  return includeAround ? { start, end: end + 1 } : { start: start + 1, end };
};

const getVimQuoteRange = (cm, head, quote, includeAround) => {
  const lineText = cm.getLine(head.line);
  const quotePositions = [];

  for (let index = 0; index < lineText.length; index += 1) {
    if (lineText[index] !== quote) continue;

    let backslashCount = 0;
    for (
      let previous = index - 1;
      previous >= 0 && lineText[previous] === "\\";
      previous -= 1
    ) {
      backslashCount += 1;
    }

    if (backslashCount % 2 === 0) quotePositions.push(index);
  }

  for (let index = 0; index + 1 < quotePositions.length; index += 2) {
    const startCharacter = quotePositions[index];
    const endCharacter = quotePositions[index + 1];
    if (head.ch < startCharacter || head.ch > endCharacter) continue;

    return {
      start: cm.indexFromPos({
        line: head.line,
        ch: startCharacter + (includeAround ? 0 : 1),
      }),
      end: cm.indexFromPos({
        line: head.line,
        ch: endCharacter + (includeAround ? 1 : 0),
      }),
    };
  }

  return null;
};

const configureSafeVimTextObjects = (CodeMirror) => {
  CodeMirror.Vim.defineMotion("sunnyVimWord", (cm, head, motionArgs, vim) => {
    const range = getVimWordRange(cm, head, motionArgs.includeAround);
    return range && vimTextObjectRange(cm, range.start, range.end, vim);
  });

  CodeMirror.Vim.defineMotion("sunnyVimBlock", (cm, head, motionArgs, vim) => {
    const range = getVimBlockRange(
      cm,
      head,
      motionArgs.opening,
      motionArgs.includeAround,
    );
    return range && vimTextObjectRange(cm, range.start, range.end, vim);
  });

  CodeMirror.Vim.defineMotion("sunnyVimQuote", (cm, head, motionArgs, vim) => {
    const range = getVimQuoteRange(
      cm,
      head,
      motionArgs.quote,
      motionArgs.includeAround,
    );
    return range && vimTextObjectRange(cm, range.start, range.end, vim);
  });

  CodeMirror.Vim.mapCommand("iw", "motion", "sunnyVimWord", {
    includeAround: false,
  });
  CodeMirror.Vim.mapCommand("aw", "motion", "sunnyVimWord", {
    includeAround: true,
  });

  const blockAliases = {
    "(": "(",
    ")": "(",
    b: "(",
    "[": "[",
    "]": "[",
    "{": "{",
    "}": "{",
    B: "{",
    "<": "<",
    ">": "<",
  };

  Object.entries(blockAliases).forEach(([key, opening]) => {
    CodeMirror.Vim.mapCommand(`i${key}`, "motion", "sunnyVimBlock", {
      opening,
      includeAround: false,
    });
    CodeMirror.Vim.mapCommand(`a${key}`, "motion", "sunnyVimBlock", {
      opening,
      includeAround: true,
    });
  });

  ['"', "'", "`"].forEach((quote) => {
    CodeMirror.Vim.mapCommand(`i${quote}`, "motion", "sunnyVimQuote", {
      quote,
      includeAround: false,
    });
    CodeMirror.Vim.mapCommand(`a${quote}`, "motion", "sunnyVimQuote", {
      quote,
      includeAround: true,
    });
  });
};

const configureVimShortcuts = (CodeMirror) => {
  CodeMirror.Vim.defineAction("sunnySelectAll", (cm) => {
    selectAllContent(cm);
  });

  CodeMirror.Vim.defineAction("sunnyCloseModal", (cm) => {
    cm.state.sunnyFlushChange?.();
    if (!cm.state.sunnyOnLeaderWrite?.(cm.getValue())) {
      cm.state.sunnyOnCloseModal?.();
    }
  });

  CodeMirror.Vim.mapCommand("<Space>a", "action", "sunnySelectAll", {});
  CodeMirror.Vim.mapCommand("<Space>w", "action", "sunnyCloseModal", {});
  CodeMirror.Vim.map("jk", "<Esc>", "insert");
  CodeMirror.Vim.map("L", "$", "normal");
  CodeMirror.Vim.map("H", "0", "normal");
};

const configureVim = (CodeMirror) => {
  if (!CodeMirror?.Vim || vimConfigured) return;

  configureSafeVimTextObjects(CodeMirror);
  configureVimShortcuts(CodeMirror);
  vimConfigured = true;
};

const insertMarkdownIndent = (editor) => {
  if (editor.somethingSelected()) {
    editor.execCommand("indentMore");
    return;
  }

  editor.replaceSelection("  ", "end");
};

const configureRelativeLineNumbers = (cm) => {
  const updateLineNumberFormatter = () => {
    const cursorLine = cm.getCursor().line + 1;

    cm.setOption("lineNumberFormatter", (lineNumber) => {
      if (lineNumber === cursorLine) return String(lineNumber);
      return String(Math.abs(lineNumber - cursorLine));
    });
  };

  updateLineNumberFormatter();
  cm.on("cursorActivity", updateLineNumberFormatter);
  cm.on("changes", updateLineNumberFormatter);

  return () => {
    cm.off("cursorActivity", updateLineNumberFormatter);
    cm.off("changes", updateLineNumberFormatter);
  };
};

const configureWheelScroll = (cm) => {
  const wrapper = cm.getWrapperElement();

  const handleWheel = (event) => {
    const scrollInfo = cm.getScrollInfo();
    const nextTop = Math.max(
      0,
      Math.min(scrollInfo.height - scrollInfo.clientHeight, scrollInfo.top + event.deltaY),
    );
    const nextLeft = Math.max(
      0,
      Math.min(scrollInfo.width - scrollInfo.clientWidth, scrollInfo.left + event.deltaX),
    );

    if (nextTop !== scrollInfo.top || nextLeft !== scrollInfo.left) {
      event.preventDefault();
      cm.scrollTo(nextLeft, nextTop);
    }
  };

  wrapper.addEventListener("wheel", handleWheel, { passive: false });

  return () => {
    wrapper.removeEventListener("wheel", handleWheel);
  };
};

const loadCodeMirrorFromCdn = () => {
  const pythonModeSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/mode/python/python.min.js";
  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        if (existing.dataset.loaded === "true") resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = false;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = (error) => {
        script.remove();
        reject(error);
      };
      document.body.appendChild(script);
    });

  if (codeMirrorLoadPromise) return codeMirrorLoadPromise;

  const styles = [
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/codemirror.min.css",
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/theme/monokai.min.css",
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/addon/dialog/dialog.min.css",
  ];
  const scripts = [
    ...(!window.CodeMirror ? ["https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/codemirror.min.js"] : []),
    ...(!window.CodeMirror?.prototype?.getSearchCursor ? ["https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/addon/search/searchcursor.min.js"] : []),
    ...(!window.CodeMirror?.prototype?.openDialog ? ["https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/addon/dialog/dialog.min.js"] : []),
    ...(!window.CodeMirror?.Vim ? ["https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/keymap/vim.min.js"] : []),
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/mode/markdown/markdown.min.js",
    pythonModeSrc,
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/mode/xml/xml.min.js",
    "https://cdnjs.cloudflare.com/ajax/libs/codemirror/5.65.20/addon/selection/active-line.min.js",
  ];

  styles.forEach((href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  });

  codeMirrorLoadPromise = scripts
    .reduce(
      (promise, src) => promise.then(() => loadScript(src)),
      Promise.resolve(),
    )
    .then(() => {
      const CodeMirror = window.CodeMirror;
      configureVim(CodeMirror);
      return CodeMirror;
    })
    .catch((error) => {
      codeMirrorLoadPromise = null;
      throw error;
    });

  return codeMirrorLoadPromise;
};

export default function VimMarkdownEditor({
  value,
  onChange,
  onCloseModal,
  onLeaderWrite,
  syntaxMode = "markdown",
}) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const cleanupEditorConfigRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onCloseModalRef = useRef(onCloseModal);
  const onLeaderWriteRef = useRef(onLeaderWrite);
  const initialValueRef = useRef(value);
  // Last text reported upward. A heavy parent can echo it back after more typing;
  // that stale echo must not overwrite the editor.
  const lastEmittedValueRef = useRef(value);
  const pendingEmitTimerRef = useRef(null);
  const [loadError, setLoadError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCloseModalRef.current = onCloseModal;
    if (editorRef.current) {
      editorRef.current.state.sunnyOnCloseModal = () => {
        onCloseModalRef.current?.();
      };
    }
  }, [onCloseModal]);

  useEffect(() => {
    onLeaderWriteRef.current = onLeaderWrite;
    if (editorRef.current) {
      editorRef.current.state.sunnyOnLeaderWrite = (code) =>
        Boolean(onLeaderWriteRef.current?.(code));
    }
  }, [onLeaderWrite]);

  useEffect(() => {
    let disposed = false;

    loadCodeMirrorFromCdn()
      .then((CodeMirror) => {
        if (disposed || !containerRef.current) return;

        editorRef.current = CodeMirror(containerRef.current, {
          value: initialValueRef.current,
          mode:
            syntaxMode === "python"
              ? { name: "python", version: 3, singleLineStringErrors: false }
              : syntaxMode === "svg"
                ? { name: "xml", htmlMode: false }
                : { name: "markdown", highlightFormatting: true },
          keyMap: "vim",
          theme: "monokai",
          lineNumbers: true,
          styleActiveLine: true,
          lineWrapping: true,
          extraKeys: {
            Tab: insertMarkdownIndent,
            "Shift-Tab": (editor) => editor.execCommand("indentLess"),
            "Ctrl-A": (editor) => editor.execCommand("selectAll"),
            "Cmd-A": (editor) => editor.execCommand("selectAll"),
            "Ctrl-C": (editor) => {
              const selection = editor.getSelection();
              if (selection) writeSystemClipboard(selection);
            },
            "Cmd-C": (editor) => {
              const selection = editor.getSelection();
              if (selection) writeSystemClipboard(selection);
            },
            "Ctrl-S": false,
            "Cmd-S": false,
          },
        });
        editorRef.current.setSize("100%", "100%");
        editorRef.current.state.sunnySyntaxMode = syntaxMode;
        editorRef.current.state.sunnyOnCloseModal = () => {
          onCloseModalRef.current?.();
        };
        editorRef.current.state.sunnyOnLeaderWrite = (code) =>
          Boolean(onLeaderWriteRef.current?.(code));

        const cleanupRelativeLineNumbers = configureRelativeLineNumbers(editorRef.current);
        const cleanupWheelScroll = configureWheelScroll(editorRef.current);
        cleanupEditorConfigRef.current = () => {
          cleanupRelativeLineNumbers();
          cleanupWheelScroll();
        };

        configureVimClipboard(CodeMirror, editorRef.current);

        const emitChange = () => {
          window.clearTimeout(pendingEmitTimerRef.current);
          pendingEmitTimerRef.current = null;
          const nextValue = editorRef.current?.getValue();
          if (nextValue == null || nextValue === lastEmittedValueRef.current) return;
          lastEmittedValueRef.current = nextValue;
          onChangeRef.current(nextValue);
        };
        editorRef.current.state.sunnyFlushChange = emitChange;
        editorRef.current.on("change", (editor) => {
          // While an insert-mode mapping such as "jk" waits for its next key, a
          // synchronous parent re-render can outlast Vim's 200 ms window and
          // drop the mapping. Report the text once the key sequence settles.
          if (editor.state.vim?.insertMode && editor.state.vim.inputState?.keyBuffer) {
            window.clearTimeout(pendingEmitTimerRef.current);
            pendingEmitTimerRef.current = window.setTimeout(emitChange, 260);
            return;
          }
          emitChange();
        });
        editorRef.current.on("vim-mode-change", emitChange);
        editorRef.current.on("blur", emitChange);

        setReady(true);
        setTimeout(() => {
          editorRef.current?.setSize("100%", "100%");
          editorRef.current?.refresh();
          window.CodeMirror?.Vim?.handleKey(editorRef.current, "i");
        }, 0);
      })
      .catch(() => setLoadError("Vim editor 載入失敗，請檢查網路或 CDN。"));

    return () => {
      disposed = true;
      setReady(false);
      editorRef.current?.state.sunnyFlushChange?.();
      cleanupEditorConfigRef.current?.();
      cleanupEditorConfigRef.current = null;
      if (editorRef.current) {
        editorRef.current.getWrapperElement().remove();
        editorRef.current = null;
      }
    };
  }, [syntaxMode]);

  useEffect(() => {
    if (value === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = value;
    if (editorRef.current && editorRef.current.getValue() !== value) {
      editorRef.current.setValue(value);
    }
  }, [value]);

  return (
    <VimEditorShell aria-busy={(!ready && !loadError) || undefined}>
      {loadError ? (
        <VimError>{loadError}</VimError>
      ) : (
        <div ref={containerRef} />
      )}
      {!ready && !loadError && <VimLoading role="status"><i aria-hidden="true" />載入 Vim…</VimLoading>}
    </VimEditorShell>
  );
}

// Paint the monokai background before CodeMirror arrives from the CDN, so the
// editor does not flash white while it loads.
const VimEditorShell = styled.div`
  position: relative;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: #272822;
  border: 1px solid #1f1f1f;
  border-radius: 8px;

  > div {
    min-height: 0;
    height: 100%;
  }

  .CodeMirror {
    height: 100%;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 14px;
    line-height: 1.6;
  }

  .CodeMirror-scroll {
    height: 100%;
    overflow: auto !important;
  }

  .CodeMirror-linenumber {
    min-width: 2.25em;
    padding: 0 8px 0 4px;
    color: #8f8f8f;
  }

  .CodeMirror-activeline .CodeMirror-linenumber {
    color: #f8f8f2;
    font-weight: 700;
  }
`;

const vimSpin = keyframes`to { transform: rotate(360deg); }`;

const VimLoading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #a6a28c;
  font-size: 13px;
  pointer-events: none;

  > i {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 999px;
    animation: ${vimSpin} .7s linear infinite;
  }
`;

const VimError = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--app-danger-bg, #fff);
  color: var(--app-danger-text, #8c1d18);
`;
